/**
 * The shopper's Shopify market, as the theme rendered it onto our `<script>` tag (D210).
 *
 * Shopify decides the market server-side and exposes it to Liquid, not to JavaScript: the
 * `localization` object (https://shopify.dev/docs/api/liquid/objects/localization) carries
 * `country.iso_code` (ISO 3166-1 alpha-2, https://shopify.dev/docs/api/liquid/objects/country),
 * `market.handle` and `market.id` (https://shopify.dev/docs/api/liquid/objects/market: "The ID of
 * the market", a string) and `language.iso_code` (an IETF language tag,
 * https://shopify.dev/docs/api/liquid/objects/shop_locale). The install line and the theme
 * snippet render them as four attributes (`docs/STOREFRONT.md` §1):
 *
 *     data-peak-country="{{ localization.country.iso_code }}"
 *     data-peak-market="{{ localization.market.handle }}"
 *     data-peak-market-id="{{ localization.market.id }}"
 *     data-peak-language="{{ localization.language.iso_code }}"
 *
 * The market id is what lets the service recognise a market whose handle was renamed
 * (`docs/MARKETS.md` §1); a line without it (every store installed before) sends the handle alone.
 *
 * This module only reads them back. Every member is optional: an install line without the
 * attributes (every store installed before D210), a theme that renders them empty, or a page
 * with no Shopify at all yields nothing, and the handoff goes out exactly as before.
 */

/**
 * The four attributes, `[payload member, attribute name]`. Current spelling only: they
 * postdate the `yc-` rename (D105), so there is no legacy name to read.
 */
export const PEAK_MARKET_ATTRIBUTES: readonly (readonly [keyof PeakMarketContext, string])[] = [
  ['country', 'data-peak-country'],
  ['handle', 'data-peak-market'],
  ['id', 'data-peak-market-id'],
  ['language', 'data-peak-language'],
];

/** The `market` block of the create payload. Absent members are left out, never empty. */
export type PeakMarketContext = {
  country?: string;
  handle?: string;
  /** Liquid `localization.market.id`, as rendered; the service validates it. */
  id?: string;
  language?: string;
  /**
   * The absolute URL `cart.js` was fetched from (`./bootstrap.ts`: `data-peak-cart-url`, else
   * `window.Shopify.routes.root` + `cart.js`), market prefix included. The service keeps it only
   * on the shop's own domains and uses it for the links back to the store (`docs/MARKETS.md`
   * §2 item 7).
   */
  cartUrl?: string;
};

/**
 * The market attributes of our tag: the members that are present and non-empty, trimmed.
 * Nothing else is checked here — the service validates every value and drops what does not
 * fit (`src/lib/markets/resolve.ts`). `undefined`, not `{}`, when there is no tag or none of
 * them is set, so the payload builder simply leaves the block out (`./cart.ts`). Kept this
 * small on purpose: the bundle has a byte budget (`tests/unit/storefront/bundle.test.ts`).
 */
export function readMarketContext(
  script: { getAttribute: (name: string) => string | null } | null,
): PeakMarketContext | undefined {
  if (script === null) return undefined;
  let context: PeakMarketContext | undefined;
  for (const [member, name] of PEAK_MARKET_ATTRIBUTES) {
    const value = (script.getAttribute(name) ?? '').trim();
    if (value) {
      context = context || {};
      context[member] = value;
    }
  }
  return context;
}

/**
 * `context` plus `cartUrl`, the cart URL the script fetched made absolute against the page
 * (`/en-gb/cart.js` → `https://shop.example/en-gb/cart.js`). Sent even without the market
 * attributes: the prefix comes from `window.Shopify.routes.root`, which every Online Store page
 * sets. A URL that does not parse leaves `context` as it was.
 */
export function withCartUrl(
  context: PeakMarketContext | undefined,
  cartUrl: string,
  base: string,
): PeakMarketContext | undefined {
  try {
    const absolute = new URL(cartUrl, base).href;
    const market = context || {};
    market.cartUrl = absolute;
    return market;
  } catch {
    // A cart URL the browser cannot resolve: the handoff goes out without it.
    return context;
  }
}
